/**
 * CloseButton - Enterprise-grade close/dismiss button component
 *
 * A professional, accessible close button with red accent styling.
 * Use this component consistently throughout the app for all close/dismiss actions.
 *
 * @example
 * ```tsx
 * // Basic usage
 * <CloseButton onClick={handleClose} />
 *
 * // With absolute positioning (for modals/overlays)
 * <CloseButton onClick={handleClose} position="absolute" />
 *
 * // Different sizes
 * <CloseButton onClick={handleClose} size="lg" />
 *
 * // Custom label for accessibility
 * <CloseButton onClick={handleClose} label="Dismiss notification" />
 * ```
 */

import type React from 'react';
import { XMarkIcon } from '@heroicons/react/24/solid';

export interface CloseButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'onClick' | 'className' | 'children' | 'type'> {
  /** Click handler. Receives the event, e.g. to stop it reaching a clickable row. */
  onClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
  /** Size variant. `xs` is for a badge on another control or a clear button inside a field. */
  size?: 'xs' | 'sm' | 'md' | 'lg';
  /** Position mode - 'static' for flow, 'absolute' for overlay positioning */
  position?: 'static' | 'absolute';
  /** Accessible label for screen readers */
  label?: string;
  /** Additional CSS classes */
  className?: string;
  /** Whether the button is disabled */
  disabled?: boolean;
}

export default function CloseButton({
  onClick,
  size = 'md',
  position = 'static',
  label = 'Close',
  className = '',
  disabled = false,
  ...rest
}: CloseButtonProps) {
  // Size configurations
  const sizeConfig = {
    xs: {
      button: 'w-5 h-5',
      icon: 'w-3 h-3',
    },
    sm: {
      button: 'w-7 h-7',
      icon: 'w-4 h-4',
    },
    md: {
      button: 'w-8 h-8',
      icon: 'w-5 h-5',
    },
    lg: {
      button: 'w-10 h-10',
      icon: 'w-6 h-6',
    },
  };

  const config = sizeConfig[size];

  // Position classes
  const positionClasses = position === 'absolute' ? 'absolute top-3 right-3 sm:top-4 sm:right-4' : '';

  return (
    <button
      {...rest}
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      className={`
        ${config.button}
        ${positionClasses}
        inline-flex items-center justify-center
        rounded-lg
        bg-red-50 hover:bg-red-100
        text-red-500 hover:text-red-600
        border border-red-200 hover:border-red-300
        transition-all duration-200 ease-out
        focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2
        disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-red-50 disabled:hover:text-red-500
        active:scale-95
        touch-manipulation
        ${className}
      `}
    >
      <XMarkIcon className={`${config.icon} transition-transform`} />
    </button>
  );
}
